import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Ticket, Check, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PromoCodeInput - Reusable promo code input with validation
 * 
 * Usage:
 * <PromoCodeInput
 *   scope="membership"  // or "marketplace" or "universal"
 *   purchaseAmount={14.99}
 *   onValidCode={(codeData) => setDiscount(codeData)}
 *   onClear={() => setDiscount(null)}
 * />
 */
const PromoCodeInput = ({
  scope = 'universal',
  purchaseAmount = null,
  membershipTier = null,
  onValidCode,
  onClear,
  className = '',
  label = 'Promo Code',
  placeholder = 'Enter promo code'
}) => {
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validatedCode, setValidatedCode] = useState(null);
  const [error, setError] = useState('');

  const validateCode = async () => {
    if (!code.trim()) {
      setError('Please enter a code');
      return;
    }

    setValidating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          scope,
          purchase_amount: purchaseAmount,
          membership_tier: membershipTier
        })
      });

      const result = await response.json();

      if (result.valid) {
        setValidatedCode(result);
        setError('');
        onValidCode?.(result);
        toast.success(result.message);
      } else {
        setValidatedCode(null);
        setError(result.message);
        onClear?.();
      }
    } catch (err) {
      setError('Failed to validate code');
      setValidatedCode(null);
      onClear?.();
    } finally {
      setValidating(false);
    }
  };

  const clearCode = () => {
    setCode('');
    setValidatedCode(null);
    setError('');
    onClear?.();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateCode();
    }
  };

  const formatDiscount = () => {
    if (!validatedCode) return '';
    if (validatedCode.discount_type === 'percentage') {
      return `${validatedCode.discount_value}% OFF`;
    }
    if (validatedCode.discount_type === 'fixed_amount') {
      return `$${validatedCode.discount_value} OFF`;
    }
    if (validatedCode.discount_type === 'free_trial') {
      return `${validatedCode.discount_value} Days Free`;
    }
    return '';
  };

  return (
    <div className={className}>
      {label && (
        <Label className="text-sm font-medium mb-2 block">{label}</Label>
      )}
      
      {!validatedCode ? (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              className="pl-10"
              disabled={validating}
            />
          </div>
          <Button
            type="button"
            onClick={validateCode}
            disabled={validating || !code.trim()}
            variant="outline"
          >
            {validating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Apply'
            )}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <div>
              <div className="flex items-center gap-2">
                <code className="font-mono font-medium text-green-800">
                  {validatedCode.code}
                </code>
                <Badge className="bg-green-100 text-green-800">
                  {formatDiscount()}
                </Badge>
              </div>
              {validatedCode.calculated_discount > 0 && (
                <p className="text-sm text-green-700">
                  You save ${validatedCode.calculated_discount.toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearCode}
            className="h-8 w-8 text-green-700 hover:text-green-900"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
          <X className="w-3 h-3" />
          {error}
        </p>
      )}

      {validatedCode?.description && !error && (
        <p className="text-sm text-muted-foreground mt-1">
          {validatedCode.description}
        </p>
      )}

      {validatedCode?.expires_at && (
        <p className="text-xs text-muted-foreground mt-1">
          Expires: {new Date(validatedCode.expires_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

export default PromoCodeInput;
